import { useId, useRef } from "react";
import { Radio, type RadioVariant } from "./Radio";

export interface RadioOption {
  value: string;
  label: string;

  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  options: RadioOption[];
  value?: string;
  onChange: (value: string) => void;

  variant?: RadioVariant;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

export function RadioGroup({
  options,
  value,
  onChange,
  variant = "default",
  disabled = false,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: Readonly<RadioGroupProps>) {
  const groupId = useId();
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectableIndexes = options.reduce<number[]>((acc, option, index) => {
    if (!option.disabled) acc.push(index);
    return acc;
  }, []);

  const commit = (index: number) => {
    const option = options[index];
    if (!option || option.disabled || disabled) return;
    onChange(option.value);
    optionRefs.current[index]?.focus();
  };

  const moveSelection = (direction: 1 | -1) => {
    const currentIndex = options.findIndex((option) => option.value === value);
    const pool = selectableIndexes;
    if (pool.length === 0) return;
    const currentPoolIndex = pool.indexOf(currentIndex);
    const nextPoolIndex = currentPoolIndex === -1 ? 0 : (currentPoolIndex + direction + pool.length) % pool.length;
    commit(pool[nextPoolIndex]);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        moveSelection(1);
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        moveSelection(-1);
        break;
      case "Home":
        event.preventDefault();
        if (selectableIndexes.length > 0) commit(selectableIndexes[0]);
        break;
      case "End":
        event.preventDefault();
        if (selectableIndexes.length > 0) commit(selectableIndexes[selectableIndexes.length - 1]);
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      onKeyDown={handleKeyDown}
      className="flex flex-col gap-2"
    >
      {options.map((option, index) => {
        const isSelected = option.value === value;
        const isDisabled = disabled || option.disabled;
        return (
          <label
            key={option.value}
            htmlFor={`${groupId}-${option.value}`}
            className={[
              "flex items-start gap-3 rounded-lg border p-3 transition-colors",
              isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-border-strong",
              isSelected ? "border-border-strong" : "border-border-default",
            ].join(" ")}
          >
            <span className="mt-0.5">
              <Radio
                id={`${groupId}-${option.value}`}
                checked={isSelected}
                onSelect={() => commit(index)}
                variant={variant}
                disabled={isDisabled}
                tabIndex={isSelected || (value === undefined && index === selectableIndexes[0]) ? 0 : -1}
                buttonRef={(node) => {
                  optionRefs.current[index] = node;
                }}
              />
            </span>
            <span>
              <span className="block text-sm font-medium text-content-primary">{option.label}</span>
              {option.description && <span className="block text-xs text-content-tertiary">{option.description}</span>}
            </span>
          </label>
        );
      })}
    </div>
  );
}
